import React, { useState } from 'react';
import { ArrowRight, ArrowLeft, FileText, Clock, Info, FileCheck, CheckCircle2, Award, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { TermsModal } from './TermsModal';
import { useFormSubmissions } from '../../../hooks/useFormSubmissions';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import type { PublicForm } from '../../../types/publicForm.types';

interface FormWelcomeProps {
  form: PublicForm;
  onStart: () => void;
  primaryColor: string;
}

export const FormWelcome: React.FC<FormWelcomeProps> = ({
  form,
  onStart,
  primaryColor,
}) => {
  const navigate = useNavigate();
  const [showTermsModal, setShowTermsModal] = useState(false);
  const { currentUser } = useCurrentUser();
  const { data: submissionsData, isLoading: isLoadingSubmissions } = useFormSubmissions(
    form.slug,
    !!currentUser // Only check submissions if user is logged in
  );

  console.log('FormWelcome - Current user:', currentUser);
  console.log('FormWelcome - Submissions data:', submissionsData);
  console.log('FormWelcome - Is loading:', isLoadingSubmissions);

  const handleBackToHome = () => {
    navigate('/');
  };

  const formatSubmissionDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) {
      return 'today';
    } else if (diffInDays === 1) {
      return 'yesterday';
    } else if (diffInDays < 7) {
      return `${diffInDays} days ago`;
    } else if (diffInDays < 30) {
      const weeks = Math.floor(diffInDays / 7);
      return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  console.log('FormWelcome - Form data:', form);
  console.log('has submissions:', submissionsData?.hasSubmissions);

  return (
    <>
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto">
          {/* Back button */}
          <button
            onClick={handleBackToHome}
            className="flex items-center text-gray-600 hover:text-gray-900 bg-[#fffffff7] backdrop-blur-sm px-3 py-1.5 rounded-lg shadow-sm mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Exit
          </button>
        <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg overflow-hidden">
          {/* Form image header if present */}
          {form.logoUrl && (
            <div className="relative h-48 bg-gray-100 overflow-hidden">
              {/* Blurred background image */}
              <div
                className="absolute inset-0 bg-cover bg-center blur-xl scale-110"
                style={{
                  backgroundImage: `url(${form.logoUrl})`,
                }}
              />

              {/* Overlay to soften the blurred background */}
              <div className="absolute inset-0 bg-white/30" />

              {/* Main image - fills either full height or width */}
              <div className="relative h-full w-full flex items-center justify-center">
                <img
                  src={form.logoUrl}
                  alt={form.name}
                  className="h-full w-full object-contain drop-shadow-lg"
                />
              </div>

              {/* Subtle gradient overlay at bottom */}
              <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white/50 to-transparent" />
            </div>
          )}

          <div className="p-8">
            {/* Form icon and title */}
            <div className="flex items-start mb-6">
              <div
                className="w-12 h-12 rounded-lg flex items-center justify-center mr-4 flex-shrink-0"
                style={{ backgroundColor: `${primaryColor}20` }}
              >
                <FileText className="h-6 w-6" style={{ color: primaryColor }} />
              </div>
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">{form.name}</h1>

                {/* Form availability dates */}
                <div className="flex items-center text-sm text-gray-600">
                  <Clock className="h-4 w-4 mr-1" />
                  <span>
                    Available from {new Date(form.startsAt).toLocaleDateString()} to {new Date(form.endsAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>

            {/* Previous submission indicator */}
            {currentUser && !isLoadingSubmissions && submissionsData?.hasSubmissions && (
              <div className="mb-6 relative overflow-hidden">
                {/* Animated background gradient */}
                <div
                  className="absolute inset-0 opacity-10"
                  style={{
                    background: `linear-gradient(135deg, ${primaryColor}, transparent)`,
                  }}
                />

                <div className="relative border border-opacity-20 rounded-xl p-6" style={{ borderColor: primaryColor }}>
                  <div className="flex items-start space-x-4">
                    {/* Success icon with animation */}
                    <div className="relative">
                      <div
                        className="absolute inset-0 animate-ping rounded-full opacity-25"
                        style={{ backgroundColor: primaryColor }}
                      />
                      <div
                        className="relative w-12 h-12 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: `${primaryColor}20` }}
                      >
                        <CheckCircle2 className="h-6 w-6" style={{ color: primaryColor }} />
                      </div>
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                            Welcome back!
                            <Award className="h-5 w-5 ml-2 text-yellow-500" />
                          </h3>
                          <p className="text-gray-600 mt-1">
                            You've already submitted this form {formatSubmissionDate(submissionsData.lastSubmission?.submittedAt || '')}.
                          </p>
                          {submissionsData.submissionCount > 1 && (
                            <p className="text-sm text-gray-500 mt-1">
                              You have {submissionsData.submissionCount} total submissions for this form.
                            </p>
                          )}
                          {submissionsData.lastSubmission?.confirmationNumber && (
                            <p className="text-sm mt-2">
                              <span className="text-gray-500">Confirmation #:</span>{' '}
                              <span className="font-mono font-semibold" style={{ color: primaryColor }}>
                                {submissionsData.lastSubmission.confirmationNumber}
                              </span>
                            </p>
                          )}
                        </div>

                        <div className="flex flex-col items-end space-y-2">
                          <span
                            className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                            style={{
                              backgroundColor: `${primaryColor}10`,
                              color: primaryColor
                            }}
                          >
                            {submissionsData.lastSubmission?.workflowState === 'accepted' ? 'Accepted' :
                             submissionsData.lastSubmission?.workflowState === 'rejected' ? 'Rejected' :
                             submissionsData.lastSubmission?.workflowState === 'being_evaluated' ? 'Under Review' :
                             'Submitted'}
                          </span>
                        </div>
                      </div>

                      <div className="mt-4 flex items-center text-sm">
                        <RefreshCw className="h-4 w-4 mr-2" style={{ color: primaryColor }} />
                        <span className="text-gray-600">
                          You can submit this form again if needed.
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Form description */}
            {form.description && (
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-2">About this form</h2>
                <div
                  className="prose prose-sm max-w-none text-gray-700"
                  dangerouslySetInnerHTML={{ __html: form.description }}
                />
              </div>
            )}

            {/* Form instructions */}
            {form.instructions && (
              <div className="mb-8 bg-gray-50 rounded-lg p-4">
                <div className="flex items-start">
                  <Info className="h-5 w-5 text-gray-600 mt-0.5 mr-2 flex-shrink-0" />
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-1">Instructions</h3>
                    <div
                      className="prose prose-sm max-w-none text-gray-700"
                      dangerouslySetInnerHTML={{ __html: form.instructions }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Submission requirements */}
            <div className="mb-8">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">What you'll need:</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center">
                  <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mr-2" />
                  Basic contact information (name, email{form.options?.requirePhone === '1' && ', phone'})
                </li>
                {(form.options?.showCompanyOnForm === '1' || form.options?.requireCompany === '1') && (
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mr-2" />
                    Company information
                  </li>
                )}
                {(form.options?.showAddressOnForm === '1' || form.options?.requireAddress === '1') && (
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mr-2" />
                    Mailing address
                  </li>
                )}
                {form.submitterSurvey?.totalPages && form.submitterSurvey.totalPages > 0 && (
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mr-2" />
                    Approximately {form.submitterSurvey.totalPages * 5} minutes to complete
                  </li>
                )}
              </ul>
            </div>

            {/* Start button */}
            <div className="flex justify-center">
              <button
                onClick={onStart}
                className="inline-flex items-center px-8 py-3 text-white rounded-lg font-medium shadow-sm hover:shadow-md transform hover:-translate-y-0.5 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2"
                style={{
                  backgroundColor: primaryColor,
                  '--tw-ring-color': primaryColor
                } as React.CSSProperties}
              >
                {submissionsData?.hasSubmissions ? (
                  <>
                    <RefreshCw className="mr-2 h-5 w-5" />
                    Submit Again
                  </>
                ) : (
                  <>
                    {form.options?.callToAction || 'Start'}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </>
                )}
              </button>
            </div>

            {/* Terms link - small and out of the way */}
            {form.terms && (
              <div className="mt-6 text-center">
                <button
                  onClick={() => setShowTermsModal(true)}
                  className="inline-flex items-center text-xs text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <FileCheck className="h-3 w-3 mr-1" />
                  View Terms and Conditions
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>

    {/* Terms Modal */}
    {form.terms && (
      <TermsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        terms={form.terms}
        formName={form.name}
      />
    )}
    </>
  );
};